'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/language'
import * as DX from './exercises/deepen'

const NeuralLMTrainer = dynamic(() => import('../_components/widgets/LanguageWidgets').then(m => m.NeuralLMTrainer), { ssr: false })

function Architecture() {
  const box = (x: number, y: number, w: number, h: number, label: string, sub: string, color: string) => (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={10} fill="var(--gd-surface)" stroke={color} strokeWidth={2} />
      <text x={x + w / 2} y={y + h / 2 - 2} textAnchor="middle" fontSize={13} fontWeight={700} fill={color}>
        {label}
      </text>
      <text x={x + w / 2} y={y + h / 2 + 14} textAnchor="middle" fontSize={10.5} fill="var(--gd-ink-3)">
        {sub}
      </text>
    </g>
  )
  return (
    <svg viewBox="0 0 640 210" className="w-full" role="img" aria-label="Neural language model architecture">
      {['e', 'm', 'm'].map((ch, i) => (
        <g key={i}>
          <rect x={20} y={20 + i * 60} width={40} height={40} rx={8} fill="var(--gd-surface)" stroke="var(--gd-blue)" strokeWidth={2} />
          <text x={40} y={46 + i * 60} textAnchor="middle" fontSize={16} fontWeight={700} fill="var(--gd-blue)">
            {ch}
          </text>
          <line x1={60} y1={40 + i * 60} x2={110} y2={40 + i * 60} stroke="var(--gd-ink-3)" strokeWidth={1.5} />
          {box(110, 22 + i * 60, 90, 36, 'embed', '10 numbers', 'var(--gd-green)')}
          <line x1={200} y1={40 + i * 60} x2={250} y2={105} stroke="var(--gd-ink-3)" strokeWidth={1.5} />
        </g>
      ))}
      {box(250, 80, 92, 50, 'concat', '30 numbers', 'var(--gd-ink)')}
      <line x1={342} y1={105} x2={372} y2={105} stroke="var(--gd-ink-3)" strokeWidth={1.5} />
      {box(372, 80, 100, 50, 'hidden · tanh', '64 neurons', 'var(--gd-green)')}
      <line x1={472} y1={105} x2={502} y2={105} stroke="var(--gd-ink-3)" strokeWidth={1.5} />
      {box(502, 80, 118, 50, 'softmax', '27 probabilities', 'var(--gd-violet)')}
      <text x={561} y={160} textAnchor="middle" fontSize={12} fill="var(--gd-amber)" fontWeight={700}>
        next: “a” 61%
      </text>
    </svg>
  )
}

export default function NeuralLanguageModel() {
  return (
    <Lesson>
      <Step>
        <H>More context, smarter guesses</H>
        <P>
          The bigram model only sees one letter. Seeing three would help enormously — after “emm”, the next letter is very
          likely “a”. But a count table for 3 letters of context needs 27 × 27 × 27 × 27 ≈ half a million cells, most of them
          never seen even once. Counting doesn’t scale.
        </P>
        <P>
          In 2003, Yoshua Bengio and colleagues proposed a neural network instead. Each context letter is turned into an{' '}
          <strong>embedding</strong>; the embeddings are joined together and passed through a hidden layer; a softmax at the end
          gives the probability of every possible next letter.
        </P>
        <Figure n="8.5" caption="The architecture: three context letters → embeddings → one long vector → hidden layer → probabilities for the next letter.">
          <Architecture />
        </Figure>
        <MB>{'\\mathbf{h} = \\tanh(W_1 [\\,E_{c_1};\\, E_{c_2};\\, E_{c_3}\\,] + b_1) \\qquad \\violet{P}(\\text{next}) = \\text{softmax}(W_2\\,\\mathbf{h} + b_2)'}</MB>
      </Step>

      <Step gate="q-generalise">
        <H>Train one right here</H>
        <P>
          Below, a small version of this model (with 2-number embeddings, so we can plot them) trains on the names dataset, live
          on your device. Watch three things: the loss dropping below the bigram model’s, the invented names getting more
          name-like, and the letters re-arranging themselves on the left.
        </P>
        <Figure n="8.6" wide caption="A neural language model training in your browser. Left: each letter’s embedding — vowels (amber) usually drift together, because they play similar roles in names. Right: loss and freshly sampled names.">
          <NeuralLMTrainer />
        </Figure>
        <Quiz
          id="q-generalise"
          question="Why do vowels end up near each other in the embedding space?"
          options={[
            { label: 'They appear in similar contexts, so similar vectors help the model predict well — and gradient descent finds that.', correct: true },
            { label: 'We told the model which letters are vowels.', why: 'Nobody told it anything except the names. The grouping emerged from training.' },
            { label: 'Their ID numbers are close together.', why: 'a, e, i, o, u are scattered through the alphabet — and the model never sees IDs as quantities anyway.' },
          ]}
          explain="This is the magic of embeddings: structure nobody programmed, discovered because it helps predict. At scale, the same pressure organises words by meaning."
        />
      </Step>

      <Step gate="nlm">
        <H>Write the model yourself</H>
        <P>
          Now build it in code with the <C>Tensor</C> classes — <C>Embedding</C>, <C>Linear</C>, <C>Adam</C>. These work just
          like the <C>Value</C> engine you wrote, but operate on whole matrices at once, so they’re fast enough to train on every
          name in a couple of seconds.
        </P>
        <Exercise
          id="nlm"
          title="A neural language model"
          {...EX.nlm}
          timeoutMs={60000}
          outputHeight={460}
          hints={[
            <>
              <C>{'const parts = range(block).map(t => emb.forward(contexts.map(c => c[t])))'}</C> then{' '}
              <C>Tensor.concatCols(parts)</C>.
            </>,
            <>
              <C>{'return output.forward(hidden.forward(x).tanh())'}</C>
            </>,
            <>
              Training step: <C>{'const idx = range(32).map(() => random.int(0, X.length))'}</C>, then{' '}
              <C>{'forward(idx.map(i => X[i])).crossEntropy(idx.map(i => Y[i]))'}</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          This little model has every ingredient of a modern language model except one: the context is a fixed 3 letters, and
          every position is treated the same way. Transformers fix both with <strong>attention</strong> (Module 9). First comes
          a network with a running memory, and then how we actually <em>use</em> a trained model to write text.
        </Callout>
      </Step>

      <Step gate="nlm-params">
        <H>Why the network wins</H>
        <P>
          Count what each approach has to store. A count table needs a row for every possible context, so it grows{' '}
          <em>exponentially</em> with the context length. The network only adds a few weights per extra letter of context, and
          because similar letters get similar embeddings, what it learns about one context helps with similar ones it has never
          seen.
        </P>
        <Exercise
          id="nlm-params"
          title="tableSize and nlmParams"
          {...DX.nlmParams}
          hints={[
            <>
              <C>{'27 ** n * 27'}</C>: one row per context, 27 numbers per row.
            </>,
            <>
              <C>{'vocab * emb + (ctx * emb * hidden + hidden) + (hidden * vocab + vocab)'}</C>
            </>,
          ]}
        />
      </Step>
    </Lesson>
  )
}
